import React, { useState } from 'react';
import { Calendar, MapPin, Users, Clock, Utensils, Tent, Music, PartyPopper, Sparkles, Camera, Plus, Minus, Calculator, Download, Check, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Import PROMENS components
import PromensHero from '../components/promens/PromensHero';
import PromensContactSection from '../components/promens/PromensContactSection';
import PromensFooter from '../components/promens/PromensFooter';

// Set up fonts for pdfMake
if (pdfMake.vfs === undefined) {
  pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;
}

const PromensOfferPage = () => {
  // State pro volitelné položky
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState([]);
  
  // Catering calculator state - simplified like Wanzl
  const [guestCount, setGuestCount] = useState(700);
  const [cateringQuantities, setCateringQuantities] = useState({
    mainDish: 1,        // Hlavní jídlo (průměr z 6 položek)
    snack: 1,           // Drobné občerstvení (průměr z 3 položek)
    drinks: 2,          // Nápoje (výchozí 2× na osobu)
    hotDrinks: 1,       // Teplé nápoje
    iceCream: 1         // Zmrzlina
  });
  
  // Catering prices per person
  const cateringPrices = {
    mainDish: 220,      // Průměrná cena hlavních jídel
    snack: 65,          // Průměrná cena občerstvení
    drinks: 45,         // Nápoje (pivo, limonáda, voda)
    hotDrinks: 60,      // Teplé nápoje (káva, frappé)
    iceCream: 65        // Zmrzlina
  };
  
  const updateCateringQuantity = (item, delta) => {
    setCateringQuantities(prev => ({
      ...prev,
      [item]: Math.max(0, prev[item] + delta)
    }));
  };
  
  const OVERHEADS_PER_PERSON = 43; // Režie: personál + vybavení (~30 000 Kč při 700 osobách)
  
  const calculateCateringTotal = () => {
    let foodTotal = 0;
    Object.keys(cateringQuantities).forEach(item => {
      foodTotal += cateringPrices[item] * cateringQuantities[item] * guestCount;
    });
    
    // Přidat režii (personál + vybavení)
    const overheads = OVERHEADS_PER_PERSON * guestCount;
    
    return foodTotal + overheads;
  };
  
  const cateringTotal = calculateCateringTotal();
  const cateringPerPerson = cateringTotal / guestCount;
  
  // Helper funkce pro aktivity
  const parseActivityPrice = (priceString) => {
    // Extrahuje číselnou hodnotu z ceny
    // Např: "12 000 Kč" -> 12000, "8 000-16 000 Kč" -> 12000 (průměr)
    const numbers = priceString.match(/\d[\d\s]*/g);
    if (!numbers) return 0;
    
    const cleanNumbers = numbers.map(n => parseInt(n.replace(/\s/g, '')));
    
    if (cleanNumbers.length === 2) {
      // Rozsah - použij průměr
      return (cleanNumbers[0] + cleanNumbers[1]) / 2;
    }
    return cleanNumbers[0] || 0;
  };
  
  const parseProgramPrice = (priceString) => {
    // Parsuje cenu programu - např: "10 000 Kč" -> 10000
    return parseInt(priceString.replace(/\s/g, '').replace('Kč', '')) || 0;
  };
  
  const toggleActivity = (activity) => {
    const activityId = activity.title;
    const isSelected = selectedActivities.some(a => a.title === activityId);
    
    if (isSelected) {
      setSelectedActivities(prev => prev.filter(a => a.title !== activityId));
    } else {
      const price = parseActivityPrice(activity.price);
      setSelectedActivities(prev => [...prev, { title: activityId, price }]);
    }
  };
  
  const calculateOptionalTotal = () => {
    const activitiesTotal = selectedActivities.reduce((sum, activity) => sum + activity.price, 0);
    const programTotal = selectedProgram.reduce((sum, prog) => sum + parseProgramPrice(prog.price), 0);
    return activitiesTotal + programTotal;
  };
  
  const optionalTotal = calculateOptionalTotal();
  
  // Produkce = 10 000 Kč (koordinace) + 20% z celkového programu (včetně základního) + aktivit
  const totalProgramWithActivities = 12000 + selectedProgram.reduce((sum, prog) => sum + parseProgramPrice(prog.price), 0) + selectedActivities.reduce((sum, activity) => sum + activity.price, 0);
  const coordinationFee = 10000; // Fixní koordinace na místě konání
  const agencyFee = Math.round(totalProgramWithActivities * 0.20); // 20% provize
  const productionFee = coordinationFee + agencyFee;
  
  // PDF Export rozpočtové tabulky
  const handleDownloadFullOfferPDF = () => {
    const inventoryTotal = 162000; // Pevná hodnota inventáře
    const programTotal = 12000 + selectedProgram.reduce((sum, prog) => sum + parseProgramPrice(prog.price), 0);
    const productionTotal = productionFee; // 20% z celkového programu a aktivit
    const basePrice = cateringTotal + inventoryTotal + programTotal + productionTotal;
    const totalWithOptional = basePrice + optionalTotal;
    
    const docDefinition = {
      content: [
        { text: '35. výročí firmy PROMENS', style: 'header', alignment: 'center', margin: [0, 0, 0, 5] },
        { text: 'Orientační rozpočet', style: 'subheader', alignment: 'center', margin: [0, 0, 0, 20] },
        
        // Základní info
        {
          text: [
            { text: 'Datum: ', bold: true }, '20. června 2026  •  ',
            { text: 'Počet osob: ', bold: true }, `${guestCount}  •  `,
            { text: 'Lokalita: ', bold: true }, 'Fotbalové hřiště u firmy PROMENS'
          ],
          alignment: 'center',
          margin: [0, 0, 0, 20],
          fontSize: 10
        },
        
        // Rozpočtová tabulka
        {
          table: {
            headerRows: 1,
            widths: ['*', 100],
            body: [
              // Header
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#2563eb', color: '#ffffff' },
                { text: 'Cena', style: 'tableHeader', fillColor: '#2563eb', color: '#ffffff', alignment: 'right' }
              ],
              
              // Catering
              [{ text: 'CATERING', bold: true, fillColor: '#eff6ff', colSpan: 2 }, {}],
              ['  • Hlavní jídlo (' + cateringQuantities.mainDish + '× na osobu)', (cateringPrices.mainDish * cateringQuantities.mainDish * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Drobné občerstvení (' + cateringQuantities.snack + '× na osobu)', (cateringPrices.snack * cateringQuantities.snack * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Nápoje (' + cateringQuantities.drinks + '× na osobu)', (cateringPrices.drinks * cateringQuantities.drinks * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Teplé nápoje (' + cateringQuantities.hotDrinks + '× na osobu)', (cateringPrices.hotDrinks * cateringQuantities.hotDrinks * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Zmrzlina (' + cateringQuantities.iceCream + '× na osobu)', (cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              ['  • Režie (personál, vybavení)', (OVERHEADS_PER_PERSON * guestCount).toLocaleString('cs-CZ') + ' Kč'],
              [{ text: 'Celkem Catering', bold: true, fillColor: '#fef3c7' }, { text: cateringTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#fef3c7', alignment: 'right' }],
              
              // Inventář
              [{ text: 'INVENTÁŘ A TECHNICKÉ VYBAVENÍ', bold: true, fillColor: '#eff6ff', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Modulární párty stan (10×30 m)', '39 000 Kč'],
              ['  • Mobilní stage 8×6m pro korporátní eventy', '25 000 Kč'],
              ['  • Pivní sety (50 ks)', '15 000 Kč'],
              ['  • Bistro stolky (50 ks)', '10 000 Kč'],
              ['  • 8x Stan 8x4m (3 500 Kč/ks)', '28 000 Kč'],
              ['  • Ozvučení a osvětlení akce', '25 000 Kč'],
              ['  • Montáž a demontáž', '10 000 Kč'],
              ['  • Doprava', '10 000 Kč'],
              [{ text: 'Celkem Inventář', bold: true, fillColor: '#dbeafe' }, { text: '162 000 Kč', bold: true, fillColor: '#dbeafe', alignment: 'right' }],
              
              // Program
              [{ text: 'PROGRAM NA PÓDIU', bold: true, fillColor: '#eff6ff', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Moderátor + DJ (12–19)', '12 000 Kč'],
              ...selectedProgram.map(prog => ['  • ' + prog.title, prog.price]),
              [{ text: 'Celkem Program', bold: true, fillColor: '#f3e8ff' }, { text: programTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#f3e8ff', alignment: 'right' }],
              
              // Produkce
              [{ text: 'PRODUKCE', bold: true, fillColor: '#eff6ff', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
              ['  • Koordinace na místě konání', '10 000 Kč'],
              ['  • Produkční zajištění (20% z celkového programu + aktivit)', agencyFee.toLocaleString('cs-CZ') + ' Kč'],
              [{ text: 'Celkem Produkce', bold: true, fillColor: '#fef3c7' }, { text: productionFee.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#fef3c7', alignment: 'right' }],
              
              // Volitelné položky (pokud nějaké vybrány)
              ...(optionalTotal > 0 ? [
                [{ text: 'VYBRANÉ VOLITELNÉ POLOŽKY', bold: true, fillColor: '#d1fae5', colSpan: 2, margin: [0, 10, 0, 0] }, {}],
                ...selectedActivities.map(activity => ['  • ' + activity.title, activity.price.toLocaleString('cs-CZ') + ' Kč']),
                [{ text: 'Celkem volitelné', bold: true, fillColor: '#d1fae5' }, { text: optionalTotal.toLocaleString('cs-CZ') + ' Kč', bold: true, fillColor: '#d1fae5', alignment: 'right' }]
              ] : []),
              
              // Celková cena
              [
                { text: 'CELKOVÁ ORIENTAČNÍ CENA', bold: true, fontSize: 12, fillColor: '#2563eb', color: '#ffffff', margin: [0, 5, 0, 5] },
                { text: totalWithOptional.toLocaleString('cs-CZ') + ' Kč', bold: true, fontSize: 12, fillColor: '#2563eb', color: '#ffffff', alignment: 'right', margin: [0, 5, 0, 5] }
              ]
            ]
          },
          layout: {
            hLineWidth: function(i, node) {
              return 0.5;
            },
            vLineWidth: function(i) {
              return 0.5;
            },
            hLineColor: function(i) {
              return '#e2e8f0';
            },
            vLineColor: function(i) {
              return '#e2e8f0';
            }
          }
        },
        
        { text: 'Ceny jsou bez DPH', fontSize: 9, italics: true, alignment: 'center', margin: [0, 10, 0, 20] },
        
        // Kontakt
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Catering / Inventář', bold: true, fontSize: 10, margin: [0, 0, 0, 3] },
                { text: 'Jakub Vodárek', fontSize: 9 },
                { text: 'Tel: 774 750 456', fontSize: 9 },
                { text: 'info@vseproakci.cz', fontSize: 9, color: '#2563eb' }
              ]
            },
            {
              width: '*',
              stack: [
                { text: 'Produkce / Program', bold: true, fontSize: 10, margin: [0, 0, 0, 3] },
                { text: 'Monika Ondruchová', fontSize: 9 },
                { text: 'Tel: 773 550 880', fontSize: 9 },
                { text: 'monika@vseproakci.cz', fontSize: 9, color: '#2563eb' }
              ]
            }
          ],
          margin: [0, 0, 0, 20]
        },
        
        { text: 'VšeProAkci - Profesionální organizace firemních akcí', alignment: 'center', fontSize: 9, margin: [0, 10, 0, 5] },
        { text: 'Nabídka platná do 20. 5. 2026', alignment: 'center', fontSize: 8, italics: true, color: '#64748b' }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          color: '#2563eb'
        },
        subheader: {
          fontSize: 14,
          color: '#64748b'
        },
        tableHeader: {
          bold: true,
          fontSize: 10
        }
      },
      defaultStyle: {
        fontSize: 9
      },
      pageMargins: [40, 40, 40, 40]
    };
    
    try {
      if (typeof pdfMake !== 'undefined' && pdfMake.createPdf) {
        pdfMake.createPdf(docDefinition).download('PROMENS_orientacni_rozpocet.pdf');
      } else {
        throw new Error('pdfMake not available');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Omlouváme se, export PDF momentálně nefunguje. Použijte prosím tlačítko "Tisk" ve vašem prohlížeči (Ctrl+P nebo Cmd+P) pro vytvoření PDF.');
      window.print();
    }
  };
  
  const handleDownloadCateringPDF = () => {
    const docDefinition = {
      content: [
        { text: '35. výročí firmy PROMENS', style: 'header', alignment: 'center', color: '#2563eb' },
        { text: 'Cateringová kalkulace', style: 'subheader', alignment: 'center', margin: [0, 10, 0, 20] },
        
        {
          text: [
            { text: 'Datum: ', bold: true }, '20. června 2026\n',
            { text: 'Lokalita: ', bold: true }, 'Fotbalové hřiště u firmy PROMENS\n',
            { text: 'Počet osob: ', bold: true }, `${guestCount}`
          ],
          margin: [0, 0, 0, 15]
        },
        
        { text: 'Hlavní jídla', style: 'sectionHeader', margin: [0, 15, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Cena/os', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Počet porcí', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Celkem', style: 'tableHeader', fillColor: '#eeeeee' }
              ],
              ['Kanadské hranolky Poutine', `${cateringPrices.poutine} Kč`, `${cateringQuantities.poutine}×${guestCount}`, `${(cateringPrices.poutine * cateringQuantities.poutine * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Quesadillas', `${cateringPrices.quesadillas} Kč`, `${cateringQuantities.quesadillas}×${guestCount}`, `${(cateringPrices.quesadillas * cateringQuantities.quesadillas * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Smash Beef Burger', `${cateringPrices.burger} Kč`, `${cateringQuantities.burger}×${guestCount}`, `${(cateringPrices.burger * cateringQuantities.burger * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Grilovaná krkovička', `${cateringPrices.krkovicka} Kč`, `${cateringQuantities.krkovicka}×${guestCount}`, `${(cateringPrices.krkovicka * cateringQuantities.krkovicka * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Grilované kuřecí prso', `${cateringPrices.kureci} Kč`, `${cateringQuantities.kureci}×${guestCount}`, `${(cateringPrices.kureci * cateringQuantities.kureci * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Grilovaný hermelín', `${cateringPrices.hermelin} Kč`, `${cateringQuantities.hermelin}×${guestCount}`, `${(cateringPrices.hermelin * cateringQuantities.hermelin * guestCount).toLocaleString('cs-CZ')} Kč`]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        
        { text: 'Občerstvení', style: 'sectionHeader', margin: [0, 15, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Cena/os', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Počet porcí', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Celkem', style: 'tableHeader', fillColor: '#eeeeee' }
              ],
              ['Párek v rohlíku', `${cateringPrices.parek} Kč`, `${cateringQuantities.parek}×${guestCount}`, `${(cateringPrices.parek * cateringQuantities.parek * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Vařená kukuřice', `${cateringPrices.kukurice} Kč`, `${cateringQuantities.kukurice}×${guestCount}`, `${(cateringPrices.kukurice * cateringQuantities.kukurice * guestCount).toLocaleString('cs-CZ')} Kč`],
              ['Hranolky s omáčkou', `${cateringPrices.hranolky} Kč`, `${cateringQuantities.hranolky}×${guestCount}`, `${(cateringPrices.hranolky * cateringQuantities.hranolky * guestCount).toLocaleString('cs-CZ')} Kč`]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        
        { text: 'Dezerty', style: 'sectionHeader', margin: [0, 15, 0, 10] },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Položka', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Cena/os', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Počet porcí', style: 'tableHeader', fillColor: '#eeeeee' },
                { text: 'Celkem', style: 'tableHeader', fillColor: '#eeeeee' }
              ],
              ['Točená zmrzlina', `${cateringPrices.iceCream} Kč`, `${cateringQuantities.iceCream}×${guestCount}`, `${(cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč`]
            ]
          },
          layout: 'lightHorizontalLines'
        },
        
        {
          table: {
            widths: ['*', 'auto'],
            body: [
              [
                { text: 'Celková cena cateringu', style: 'totalLabel', bold: true },
                { text: `${cateringTotal.toLocaleString('cs-CZ')} Kč`, style: 'totalValue', bold: true }
              ],
              [
                { text: 'Cena na osobu', style: 'perPersonLabel' },
                { text: `${Math.round(cateringPerPerson).toLocaleString('cs-CZ')} Kč`, style: 'perPersonValue' }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 15, 0, 10]
        },
        
        { text: 'Ceny jsou uvedeny bez DPH', fontSize: 10, italics: true, color: '#666666', margin: [0, 20, 0, 0] },
        
        { text: '\n\nKontakt', style: 'sectionHeader', margin: [0, 20, 0, 10] },
        {
          text: [
            { text: 'Vše pro akci s.r.o.\n', bold: true },
            'Telefon: 774 750 456\n',
            'Email: info@vseproakci.cz\n',
            'Web: www.vseproakci.cz'
          ]
        }
      ],
      styles: {
        header: { fontSize: 22, bold: true },
        subheader: { fontSize: 16, color: '#666666' },
        sectionHeader: { fontSize: 14, bold: true, color: '#2563eb' },
        tableHeader: { bold: true, fontSize: 11 },
        totalLabel: { fontSize: 14 },
        totalValue: { fontSize: 16, color: '#2563eb' },
        perPersonLabel: { fontSize: 11, color: '#666666' },
        perPersonValue: { fontSize: 12, color: '#666666' }
      }
    };
    
    try {
      if (typeof pdfMake !== 'undefined' && pdfMake.createPdf) {
        pdfMake.createPdf(docDefinition).download('PROMENS_catering_kalkulace.pdf');
      } else {
        throw new Error('pdfMake not available');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Omlouváme se, export PDF momentálně nefunguje. Použijte prosím tlačítko "Tisk" ve vašem prohlížeči (Ctrl+P nebo Cmd+P) pro vytvoření PDF.');
      window.print();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <PromensHero />


      {/* Reference z našich akcí */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-orange-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Reference z našich akcí
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Podívejte se na podobné akce, které jsme realizovali
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Fatra 90 let */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-orange-100">
              <div className="p-4">
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  🎉 FATRA a.s. - Oslavy 90 let
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Velkolepá firemní oslava výročí s kompletní produkcí
                </p>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/QFfg6mOml4s"
                    title="FATRA, a.s. oslavy 90 let"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {/* KCK Cyklosport Zlín */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-orange-100">
              <div className="p-4">
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  🚴 KCK Cyklosport Zlín - Oslavy 30 let
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Kompletní realizace akce na klíč - všeproakci.cz
                </p>
                <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/9-H7iHXLIWo"
                    title="Realizace akce na klíč - všeproakci.cz"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* O akci */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 text-slate-800">
            Co připravujeme
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Tent className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-slate-800">Zajištění inventáře, stanů a další techniky</h3>
              <p className="text-slate-600 text-sm">Kompletní technické zabezpečení akce</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <PartyPopper className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-slate-800">Program pro celou rodinu</h3>
              <p className="text-slate-600 text-sm">Aktivity pro děti i dospělé</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Music className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-slate-800">Festivalová atmosféra</h3>
              <p className="text-slate-600 text-sm">Venkovní stage s živou hudbou a programem</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-slate-100">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <Utensils className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-slate-800">Street food catering</h3>
              <p className="text-slate-600 text-sm">Food trucky a live cooking stanoviště</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gastronomická zóna - Trucky a Technika */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-blue-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%232563eb' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-full mb-6">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-lg">Naše technika</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Kompletní gastronomická zóna
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Vytvoříme pro vás <strong className="text-blue-600">profesionální street food zónu</strong> s food trucky, 
              live cooking stanovišti a sweet truckem. <strong>Festivalový zážitek</strong> pro všechny hosty.
            </p>
          </div>

          {/* Trucks Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              {
                icon: '🚚',
                count: "2×",
                name: "Food Truck",
                description: "Mobilní kuchyně pro burgery, hot dogy a grilované speciality",
                image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/j7trbdid_food-truck-mercedes-POUTINE-1-scaled.jpg"
              },
              {
                icon: '🍳',
                count: "2×",
                name: "Grill Point",
                description: "Stan pro přípravu a výdej grilovaných pokrmů",
                image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/7ne4quvg_290807044_434303445370941_8352434526447174514_n-3.jpg"
              },
              {
                icon: '🍺',
                count: "2×",
                name: "Výčepní vůz XXL",
                description: "Špičkově vybavený nápojový bar s kapacitou pro velké akce",
                image: "/vycepni-vuz-xxl.jpg"
              },
              {
                icon: '🍰',
                count: "1×",
                name: "Sweet Truck",
                description: "Italská točená zmrzlina, dezerty a sladké pochoutky",
                image: "https://vseproakci.cz/wp-content/uploads/2025/01/Snimek-obrazovky-2025-01-12-v-16.49.06.png"
              }
            ].map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-blue-100"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-gray-100">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
                    {service.count}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="text-4xl">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {service.name}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Highlights */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-3">
                <div className="text-5xl font-bold text-blue-200">7×</div>
                <div className="text-lg font-medium">Gastronomické jednotky</div>
                <div className="text-blue-100 text-sm">2× Food Truck, 2× Grill Point, 2× Výčepní vůz XXL, Sweet Truck</div>
              </div>
              
              <div className="space-y-3">
                <div className="text-5xl font-bold text-blue-200">700</div>
                <div className="text-lg font-medium">Osob současně</div>
                <div className="text-blue-100 text-sm">Kapacita obsloužit najednou</div>
              </div>
              
              <div className="space-y-3">
                <div className="text-5xl font-bold text-blue-200">15+</div>
                <div className="text-lg font-medium">Let zkušeností</div>
                <div className="text-blue-100 text-sm">S firemními akcemi</div>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/20 text-center">
              <p className="text-lg text-blue-50 italic">
                "Profesionální zázemí, které udělá z vašeho výročí nezapomenutelný zážitek"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Catering - Menu s obrázky */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Nabídka pokrmů
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Street food koncept s různorodou nabídkou pro všechny generace
          </p>
          
          {/* Hlavní jídla */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">Hlavní jídla</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: "Kanadské hranolky Poutine",
                  description: "Hranolky, trhané maso, salát coleslaw, červená cibule, smažená cibulka, BBQ omáčka, koktejlová omáčka",
                  price: 210,
                  weight: "200g",
                  image: "https://vseproakci.cz/wp-content/uploads/2025/04/VSE_PRO_AKCI_HRANOLKY_017-370x370.jpg"
                },
                {
                  name: "Quesadillas",
                  description: "Zapečená tortilla s čedarem, mozzarellou, pomalu pečeným trhaným masem v al pastor marinádě. Podáváme se salsou Mexicana a smetanou.",
                  price: 210,
                  weight: "200g",
                  image: "https://vseproakci.cz/wp-content/uploads/2025/01/quesa.jpg",
                  note: "🌱 3 varianty: klasická, vegetariánská, bez lepku a laktózy"
                },
                {
                  name: "Smash Beef Burger",
                  description: "Šťavnatý hovězí burger z irského masa s čedarem, zeleninou a domácí bulkou",
                  price: 210,
                  weight: "200g",
                  image: "https://vseproakci.cz/wp-content/uploads/2025/01/beef-burger-2.png"
                },
                {
                  name: "Grilovaná krkovička",
                  description: "Marinovaná v naší speciální marinádě + příloha grenaille brambory",
                  price: 199,
                  weight: "250g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/y27d7jww_grilovana%20krkovicka.jpg"
                },
                {
                  name: "Grilované kuřecí prso",
                  description: "Šťavné kuřecí prso marinované v bylinkách, opečené na grilu s lehkou medovou glazurou a s grilovanou zeleninou",
                  price: 189,
                  weight: "200g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/14h4prs9_grilovane%20kureci%20prso.jpg"
                },
                {
                  name: "Grilovaný hermelín",
                  description: "Grilovaný hermelín, zeleninový salátek s medovo-hořčičným dresingem, brusinky + bagetka",
                  price: 159,
                  weight: "150g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9t13ai9e_hermelin.jpg",
                  vegetarian: true
                }
              ].map((dish, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-56 overflow-hidden bg-gray-100">
                    <img
                      src={dish.image}
                      alt={dish.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {dish.vegetarian && (
                      <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                        <Sparkles className="w-4 h-4" />
                        <span>Vegetariánské</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      {dish.name}
                    </h4>
                    <p className="text-gray-600 mb-3 text-sm leading-relaxed">
                      {dish.description}
                    </p>
                    {dish.note && (
                      <p className="text-xs text-green-600 font-medium mb-3">
                        {dish.note}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Občerstvení */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-800 mb-6">Občerstvení</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  name: "Párek v rohlíku",
                  description: "Klasický párek v čerstvém rohlíku s hořčicí a kečupem",
                  price: 50,
                  weight: "80g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/yurb3id6_parek%20v%20rohliku.png"
                },
                {
                  name: "Vařená kukuřice",
                  description: "Čerstvá vařená kukuřice s máslem a solí",
                  price: 65,
                  weight: "150g",
                  image: "https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/6igmo933_varena%20kukurice.jpg",
                  vegetarian: true
                },
                {
                  name: "Hranolky s omáčkou",
                  description: "Křupavé zlaté hranolky servírované s kečupem nebo domácí tatarskou omáčkou",
                  price: 60,
                  weight: "100g",
                  image: "https://images.unsplash.com/photo-1518013431117-eb1465fa5752?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxjcmlzcHklMjBmcmVuY2glMjBmcmllcyUyMHdpdGglMjBkaXBwaW5nJTIwc2F1Y2UlMjBmb29kJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzczMDQ3NTExfDA&ixlib=rb-4.1.0&q=85",
                  vegetarian: true
                }
              ].map((snack, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img
                      src={snack.image}
                      alt={snack.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                    />
                    {snack.vegetarian && (
                      <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                        <Sparkles className="w-4 h-4" />
                        <span>Vegetariánské</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      {snack.name}
                    </h4>
                    <p className="text-gray-600 mb-3 text-sm">
                      {snack.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Price Calculator - Orientační cena */}
      <section className="py-16 px-4 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-full mb-6">
              <Calculator className="w-6 h-6" />
              <span className="font-bold text-lg">Cenová kalkulačka</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Orientační cena dle počtu osob
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Upravte počet hostů a zjistěte okamžitě orientační cenu pro kompletní catering službu
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-blue-100">
            {/* Guest Counter */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <Users className="w-8 h-8" />
                <label className="text-2xl font-bold">Počet hostů</label>
              </div>
              <div className="flex items-center justify-center space-x-6">
                <button
                  onClick={() => setGuestCount(Math.max(50, guestCount - 50))}
                  className="bg-white/20 hover:bg-white/30 rounded-full w-14 h-14 flex items-center justify-center text-3xl font-bold transition-all duration-200 backdrop-blur-sm"
                >
                  −
                </button>
                <input
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(Math.max(50, parseInt(e.target.value) || 300))}
                  className="w-40 text-center text-5xl font-bold bg-white/20 backdrop-blur-sm rounded-2xl py-4 border-2 border-white/30 focus:outline-none focus:border-white"
                />
                <button
                  onClick={() => setGuestCount(guestCount + 50)}
                  className="bg-white/20 hover:bg-white/30 rounded-full w-14 h-14 flex items-center justify-center text-3xl font-bold transition-all duration-200 backdrop-blur-sm"
                >
                  +
                </button>
              </div>
            </div>
            
            {/* Price Summary */}
            <div className="p-8">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Kompletní servis zahrnuje</h3>
                
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Pokrmy a nápoje</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Kompletní nabídka pokrmů z food trucku a live cooking stanoviště</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Zmrzlina ze sweet trucku</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Nápoje z výčepního vozu</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Všechny pokrmy připravované čerstvě na místě</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Profesionální personál</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Zkušení kuchaři pro food truck a live cooking</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Profesionální barmani a personál sweet trucku</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Pomocný personál pro plynulý chod akce</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Kompletní servis a úklid po akci</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Technika a vybavení</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>7× gastronomických jednotek (2× Food truck, 2× Grill Point, 2× Výčepní vůz XXL, Sweet truck)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Veškeré gastro vybavení, nádobí a spotřební materiál</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 text-xl">✓</span>
                      <span>Doprava, instalace a demontáž veškeré techniky</span>
                    </li>
                  </ul>
                </div>
                
                {/* Rozpad ceny jídel */}
                <div className="bg-white rounded-xl p-6 border-2 border-blue-200">
                  <h4 className="font-bold text-gray-900 mb-4 text-lg">Rozpad ceny pokrmů a nápojů (počet na osobu)</h4>
                  <div className="space-y-3">
                    {/* Hlavní jídlo */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Hlavní jídlo</span>
                          <p className="text-xs text-gray-500 mt-1">Poutine, Quesadillas, Burger, Krkovička, Kuřecí prso, Hermelín</p>
                        </div>
                        <span className="text-blue-600 font-bold">220 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('mainDish', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.mainDish}x</span>
                          <button
                            onClick={() => updateCateringQuantity('mainDish', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.mainDish * cateringQuantities.mainDish * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Drobné občerstvení */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Drobné občerstvení</span>
                          <p className="text-xs text-gray-500 mt-1">Párek v rohlíku, Vařená kukuřice, Hranolky s omáčkou</p>
                        </div>
                        <span className="text-blue-600 font-bold">50 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('snack', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.snack}x</span>
                          <button
                            onClick={() => updateCateringQuantity('snack', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.snack * cateringQuantities.snack * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Nápoje */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Nápoj</span>
                          <p className="text-xs text-gray-500 mt-1">Pivo Radegast, točená limonáda, voda</p>
                        </div>
                        <span className="text-blue-600 font-bold">45 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('drinks', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.drinks}x</span>
                          <button
                            onClick={() => updateCateringQuantity('drinks', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.drinks * cateringQuantities.drinks * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Teplé nápoje */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="text-gray-700 font-medium">Teplý nápoj</span>
                          <p className="text-xs text-gray-500 mt-1">Káva / Frappé</p>
                        </div>
                        <span className="text-blue-600 font-bold">60 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('hotDrinks', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.hotDrinks}x</span>
                          <button
                            onClick={() => updateCateringQuantity('hotDrinks', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.hotDrinks * cateringQuantities.hotDrinks * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Zmrzlina */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-medium">Točená zmrzlina</span>
                        <span className="text-blue-600 font-bold">50 Kč</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateCateringQuantity('iceCream', -1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            −
                          </button>
                          <span className="text-lg font-bold w-12 text-center">{cateringQuantities.iceCream}x</span>
                          <button
                            onClick={() => updateCateringQuantity('iceCream', 1)}
                            className="bg-blue-100 hover:bg-blue-200 rounded-full w-8 h-8 flex items-center justify-center font-bold transition-colors"
                          >
                            +
                          </button>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                    
                    {/* Režie - Personál a vybavení */}
                    <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-gray-700 font-medium">🛠️ Režie (Personál + Vybavení)</span>
                          <p className="text-xs text-gray-500 mt-1">Kompletní zajištění akce</p>
                        </div>
                        <span className="font-semibold text-gray-900">
                          {(OVERHEADS_PER_PERSON * guestCount).toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Important Notice */}
                <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-200">
                  <h4 className="font-bold text-amber-900 mb-3 text-lg flex items-center gap-2">
                    <span className="text-2xl">⚠️</span>
                    Důležité upozornění
                  </h4>
                  <ul className="space-y-2 text-amber-900">
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-2 font-bold">•</span>
                      <span><strong>Hlavní jídlo i nápoje se vydávají oproti poukazu</strong></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-amber-600 mr-2 font-bold">•</span>
                      <span>Pokud by si zákazník přál <strong>neomezenou spotřebu nápojů</strong>, nabízíme variantu bez poukazů</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Total Price */}
              <div className="mt-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                <div className="text-center">
                  <p className="text-blue-100 text-sm mb-2">Orientační celková cena pro {guestCount} osob</p>
                  <p className="text-5xl font-bold">{(cateringTotal).toLocaleString('cs-CZ')} Kč</p>
                  <p className="text-blue-100 text-sm mt-2">{Math.round(cateringPerPerson).toLocaleString('cs-CZ')} Kč na osobu</p>
                </div>
                <p className="text-blue-100 text-xs mt-6">* Součástí celkové ceny jsou režie (technika a vybavení, personál vč. dopravy)</p>
                <p className="text-blue-100 text-xs">* Ceny jsou orientační a mohou se lišit dle finální specifikace požadavků</p>
                <p className="text-blue-100 text-xs">* Ceny jsou uvedeny bez DPH</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inventář */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Inventář a technické vybavení
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Kompletní zajištění prostor a techniky pro 700 hostů
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/pvia546e_modularnipartystan.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Tent className="w-7 h-7 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white drop-shadow-lg">Modulární párty stan</h3>
                  <p className="text-white text-sm mb-3 drop-shadow-lg">
                    10 × 30 m (možnost rozdělení na dva stany 10x15)
                  </p>
                  <div className="text-2xl font-bold text-white bg-blue-600/80 px-3 py-1 rounded-lg inline-block drop-shadow-lg">39 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9bd2dmlb_stage-s-pa-wings-REKLAMA-1.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Music className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white">Mobilní stage 8x6m pro korporátní eventy</h3>
                  <p className="text-gray-200 text-sm mb-3">
                    S možností brandingu
                  </p>
                  <div className="text-2xl font-bold text-white bg-purple-600/80 px-3 py-1 rounded-lg inline-block">25 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-7 h-7 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-slate-800">Pivní sety</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    50 kusů kompletních setů
                  </p>
                  <div className="text-2xl font-bold text-amber-600">15 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-7 h-7 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-slate-800">Bistro stolky</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    50 kusů
                  </p>
                  <div className="text-2xl font-bold text-green-600">10 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/itfnf7n6_8x4m-logo-1-scaled.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Tent className="w-7 h-7 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white drop-shadow-lg">8x Stan 8x4m</h3>
                  <p className="text-white text-sm mb-3 drop-shadow-lg">
                    3 500 Kč / ks (možnost spojení stanů) např. 2x stan 8x16
                  </p>
                  <div className="text-2xl font-bold text-white bg-orange-600/80 px-3 py-1 rounded-lg inline-block drop-shadow-lg">28 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/9gyn0bcw_stage-8x6-v2-1.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Music className="w-7 h-7 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white">Ozvučení a osvětlení akce</h3>
                  <p className="text-gray-200 text-sm mb-3">
                    Kompletní ozvučení a osvětlení pro celou akci
                  </p>
                  <div className="text-2xl font-bold text-white bg-red-600/80 px-3 py-1 rounded-lg inline-block">25 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow relative overflow-hidden min-h-[280px]"
              style={{
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/c8nxbusj_montaz.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
              <div className="flex items-start gap-4 mb-4 relative z-10">
                <div className="w-14 h-14 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-7 h-7 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-white drop-shadow-lg">Montáž a demontáž</h3>
                  <p className="text-white text-sm mb-3 drop-shadow-lg">
                    Kompletní montáž a demontáž veškerého vybavení
                  </p>
                  <div className="text-2xl font-bold text-white bg-indigo-600/80 px-3 py-1 rounded-lg inline-block drop-shadow-lg">10 000 Kč</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Camera className="w-7 h-7 text-cyan-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-slate-800">Doprava</h3>
                  <p className="text-slate-600 text-sm mb-3">
                    Doprava veškerého vybavení
                  </p>
                  <div className="text-2xl font-bold text-cyan-600">10 000 Kč</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h4 className="text-lg font-semibold text-slate-800 mb-1">Celkem inventář</h4>
                <p className="text-slate-600 text-sm">Kompletní technické vybavení akce</p>
              </div>
              <div className="text-3xl font-bold text-blue-600">162 000 Kč</div>
            </div>
          </div>
        </div>
      </section>

      {/* Program na pódiu */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Program na pódiu
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Základní program + výběr z různorodého doplňkového programu – sestavíme na míru
          </p>
          
          {/* Pevný program - Moderátor + DJ */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
              <Check className="w-6 h-6 text-green-600 mr-2" />
              Základní program (v ceně)
            </h3>
            <div className="bg-white p-8 rounded-xl shadow-md border-2 border-green-500">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Music className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-slate-800 mb-1">Moderátor + DJ</h4>
                    <p className="text-slate-500 text-sm">⏰ 12:00–19:00</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-green-600">12 000 Kč</div>
              </div>
            </div>
          </div>
          
          {/* Volitelný program */}
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              Volitelný doplňkový program
            </h3>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-center text-blue-800 text-sm font-medium">
                💡 <strong>Tip:</strong> Kliknutím na položku ji přidáte do celkového rozpočtu
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                title: 'Písničkář Honza Grebík', 
                time: 'Koncert', 
                price: '10 000 Kč', 
                icon: '🎸', 
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/1fi11gh0_grebik%20foto.jpg'
              },
              { 
                title: 'Kouzelník Mišuge', 
                time: 'Pro děti', 
                price: '10 000 Kč', 
                icon: '🎩',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/h3q5yb9p_mis%CC%8Cuge.jpg'
              },
              { 
                title: 'Klaun Bublín', 
                time: 'Interaktivní show', 
                price: '8 000 Kč', 
                icon: '🤡',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/zq1s12i6_bublin.jpg'
              },
              { 
                title: 'Bubenický workshop', 
                time: 'Zapojení hostů', 
                price: '8 500 Kč', 
                icon: '🥁',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/02v70c3x_bubenicky%20workshop%20s.jpg'
              },
              { 
                title: 'Taneční vystoupení', 
                time: '+ workshop pro děti', 
                price: '7 000 Kč', 
                icon: '💃',
                hasPhoto: true,
                photoUrl: 'https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/z5v0fwbg_tanecnicky%20workshop.jpg'
              },
            ].map((item, index) => {
              const isSelected = selectedProgram.some(p => p.title === item.title);
              return (
              <div 
                key={index} 
                onClick={() => {
                  if (isSelected) {
                    setSelectedProgram(selectedProgram.filter(p => p.title !== item.title));
                  } else {
                    setSelectedProgram([...selectedProgram, item]);
                  }
                }}
                className={`relative overflow-hidden min-h-[280px] rounded-xl shadow-md hover:shadow-lg transition-all cursor-pointer border-2 ${
                  isSelected ? 'border-green-500 ring-4 ring-green-200' : 'border-slate-100 hover:border-purple-300'
                }`}
                style={{
                  backgroundImage: `url(${item.photoUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center top'
                }}
              >
                {isSelected && (
                  <div className="absolute top-4 right-4 bg-green-500 text-white rounded-full p-2 shadow-lg z-10">
                    <Check className="w-5 h-5" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl"></div>
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <h3 className="text-xl font-bold text-white mb-2 drop-shadow-lg">
                    {item.title}
                  </h3>
                  <p className="text-sm text-white mb-3 drop-shadow-lg">
                    {item.time}
                  </p>
                  <div className="text-lg font-bold text-white drop-shadow-lg bg-purple-600/80 px-3 py-1 rounded inline-block">
                    {item.price}
                  </div>
                </div>
              </div>
            );
            })}
          </div>
          </div>
        </div>
      </section>

      {/* Program mimo pódium */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Aktivity mimo pódium
          </h2>
          <p className="text-center text-slate-600 mb-4 max-w-2xl mx-auto">
            Bohatá nabídka aktivit pro všechny generace
          </p>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-12 max-w-2xl mx-auto">
            <p className="text-center text-blue-800 text-sm font-medium">
              💡 <strong>Tip:</strong> Kliknutím na aktivitu ji přidáte do celkového rozpočtu
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              { title: 'Cirkusový workshop - 5 stanovišť', price: '16 000 Kč', icon: '🎪' },
              { title: 'Cirkusový workshop - 8 stanovišť', price: '23 000 Kč', icon: '🎪' },
              { title: 'Mobilní kasino', price: '21 000 Kč', icon: '🎰' },
              { title: 'Skákací hrady', price: '8 000-16 000 Kč', icon: '🏰' },
              { title: 'Řetízkový kolotoč', price: '12 000 Kč', icon: '🎠' },
              { title: 'Horolezecká stěna + 3 instruktoři', price: '18 000 Kč', icon: '🧗' },
              { title: 'Bublinová dílnička', price: '3 500 Kč', icon: '🫧' },
              { title: 'Bodyzorbing 8x', price: '10 000 Kč', icon: '🤸' },
              { title: 'Netradiční pětiboj', price: '10 000 Kč', icon: '🎯' },
              { title: 'Jízda na ponících 4hod', price: '8 000 Kč', icon: '🐴' },
              { title: 'Fotokoutek', price: '14 000 Kč', icon: '📸' },
              { title: 'Virtuální realita 3hod', price: '12 000 Kč', icon: '🥽' },
              { title: 'Lukostřelba 4hod', price: '14 000 Kč', icon: '🏹' },
              { title: 'Badminton', price: '500 Kč', icon: '🏸' },
              { title: 'Fotbal', price: '500 Kč', icon: '⚽' },
              { title: 'Stolní fotbal', price: '2 500 Kč', icon: '⚽' },
              { title: 'Simulátor F1 3hod', price: '12 000 Kč', icon: '🏎️' },
              { title: 'Dětská stezka', price: '30 000 Kč', icon: '🎯' },
              { title: 'Segway vozítka', price: '6 000 Kč/ks', icon: '🛴' },
              { title: 'Tvořivá dílna', price: '3 000-10 000 Kč', icon: '🎨' },
              { title: 'Hasiči + pěnová show', price: '10 000 Kč', icon: '🚒' },
            ].map((item, index) => {
              const isSelected = selectedActivities.some(a => a.title === item.title);
              
              return (
                <div 
                  key={index} 
                  onClick={() => toggleActivity(item)}
                  className={`bg-white rounded-lg p-6 text-center transition-all cursor-pointer border-2 relative ${
                    isSelected 
                      ? 'border-green-500 bg-green-50 shadow-lg' 
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3 z-20 bg-green-500 text-white rounded-full p-2">
                      <Check className="w-5 h-5" />
                    </div>
                  )}
                  <div className="text-6xl mb-4">{item.icon}</div>
                  <h4 className="text-sm font-semibold mb-2 text-slate-800 min-h-[40px] flex items-center justify-center">
                    {item.title}
                  </h4>
                  <p className="text-blue-600 font-semibold text-sm">
                    {item.price}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video sekce */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">


      {/* Cirkusová zóna - ukázka aktivity "Cirkusový workshop - 8 stanovišť" */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-purple-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
              🎪 Ukázka aktivity
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Cirkusový workshop - 8 stanovišť
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Interaktivní stanoviště kde si každý vyzkouší různé cirkusové disciplíny
          </p>
          
          {/* Cirkusové disciplíny */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {[
              { title: 'Zavěšená akrobacie', desc: 'Šála / obruč', icon: '🎪' },
              { title: 'Balanční stanoviště', desc: 'Koule, jednokolky', icon: '🎯' },
              { title: 'Chůdy', desc: 'Slalomová dráha', icon: '🦘' },
              { title: 'Žonglování', desc: 'Míčky, kužely', icon: '🤹' },
              { title: 'Točení', desc: 'Polky, vlajky', icon: '🎨' },
              { title: 'Taneční stanoviště', desc: 'Služky, křídla', icon: '🦋' },
              { title: 'Klaunské talifky', desc: 'Točení talířků', icon: '🎭' },
              { title: 'Slackline', desc: 'Chůze po laně', icon: '⚡' },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow border border-purple-100">
                <div className="text-5xl mb-3">{item.icon}</div>
                <h4 className="text-sm font-semibold mb-1 text-slate-800">{item.title}</h4>
                <p className="text-xs text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
          
          {/* YouTube video */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-purple-100 p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-4 text-center">
                🎬 Podívejte se, jak to vypadá
              </h3>
              <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/g96PZGtpHpk?si=eABwjTczNhYawn5V"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
              <Camera className="w-5 h-5 text-blue-600" />
              <span className="text-blue-700 font-semibold text-sm">Video ukázka</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-slate-800">
              Ukázka z podobné akce
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Podívejte se, jak probíhají naše firemní akce a jakou atmosféru dokážeme vytvořit
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 p-4">
            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/y9v7Yq1D4Mk"
                title="Ukázka z firemní akce"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Video ukazuje atmosféru a kvalitu našich firemních eventů
            </p>
          </div>
        </div>
      </section>

      {/* Rozpočet */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 text-slate-800">
            Orientační rozpočet
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Přehled nákladů na celou akci včetně produkce
          </p>
          
          <div className="space-y-6">
            {/* Catering */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Utensils className="w-5 h-5" />
                  Catering
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Hlavní jídlo ({cateringQuantities.mainDish}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.mainDish * cateringQuantities.mainDish * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Drobné občerstvení ({cateringQuantities.snack}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.snack * cateringQuantities.snack * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Nápoje ({cateringQuantities.drinks}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.drinks * cateringQuantities.drinks * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Teplé nápoje ({cateringQuantities.hotDrinks}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.hotDrinks * cateringQuantities.hotDrinks * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Zmrzlina ({cateringQuantities.iceCream}× na osobu)</span>
                  <span className="font-semibold text-slate-800">{(cateringPrices.iceCream * cateringQuantities.iceCream * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Režie (personál, vybavení, technika)</span>
                  <span className="font-semibold text-slate-800">{(OVERHEADS_PER_PERSON * guestCount).toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-orange-200">
                  <span className="text-slate-900 font-bold">Celkem Catering</span>
                  <span className="font-bold text-orange-600 text-lg">{cateringTotal.toLocaleString('cs-CZ')} Kč</span>
                </div>
              </div>
            </div>
            
            {/* Inventář */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Tent className="w-5 h-5" />
                  Inventář a technické vybavení
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Modulární párty stan (10×30 m)</span>
                  <span className="font-semibold text-slate-800">39 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Mobilní stage 8×6m</span>
                  <span className="font-semibold text-slate-800">25 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Pivní sety (50 ks)</span>
                  <span className="font-semibold text-slate-800">15 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Bistro stolky (50 ks)</span>
                  <span className="font-semibold text-slate-800">10 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">8x Stan 8x4m (3 500 Kč/ks)</span>
                  <span className="font-semibold text-slate-800">28 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Ozvučení a osvětlení akce</span>
                  <span className="font-semibold text-slate-800">25 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Montáž a demontáž</span>
                  <span className="font-semibold text-slate-800">10 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Doprava</span>
                  <span className="font-semibold text-slate-800">10 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-blue-200">
                  <span className="text-slate-900 font-bold">Celkem Inventář</span>
                  <span className="font-bold text-blue-600 text-lg">162 000 Kč</span>
                </div>
              </div>
            </div>
            
            {/* Program na pódiu */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Music className="w-5 h-5" />
                  Program na pódiu
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Moderátor + DJ (12–19)</span>
                  <span className="font-semibold text-slate-800">12 000 Kč</span>
                </div>
                {selectedProgram.map((prog, index) => (
                  <div key={index} className="flex justify-between items-center pb-2 border-b border-slate-100">
                    <span className="text-slate-700">{prog.title}</span>
                    <span className="font-semibold text-slate-800">{parseProgramPrice(prog.price).toLocaleString('cs-CZ')} Kč</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-purple-200">
                  <span className="text-slate-900 font-bold">Celkem Program na pódiu</span>
                  <span className="font-bold text-purple-600 text-lg">{(12000 + selectedProgram.reduce((sum, prog) => sum + parseProgramPrice(prog.price), 0)).toLocaleString('cs-CZ')} Kč</span>
                </div>
              </div>
            </div>

            {/* Produkce */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Produkce
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Koordinace na místě konání</span>
                  <span className="font-semibold text-slate-800">10 000 Kč</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                  <span className="text-slate-700">Produkční zajištění (20%)</span>
                  <span className="font-semibold text-slate-800">{agencyFee.toLocaleString('cs-CZ')} Kč</span>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800">
                  💡 20% z celkového programu na pódiu (včetně základního) + vybraných aktivit
                </div>
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-amber-200">
                  <span className="text-slate-900 font-bold">Celkem Produkce</span>
                  <span className="font-bold text-amber-600 text-lg">{productionFee.toLocaleString('cs-CZ')} Kč</span>
                </div>
              </div>
            </div>

            {/* Volitelné položky (pokud nějaké vybrány) */}
            {selectedActivities.length > 0 && (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <PartyPopper className="w-5 h-5" />
                    Vybrané volitelné položky
                  </h3>
                </div>
                <div className="p-6 space-y-3">
                  {selectedActivities.map((activity, index) => (
                    <div key={index} className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <span className="text-slate-700">{activity.title}</span>
                      <span className="font-semibold text-slate-800">{activity.price.toLocaleString('cs-CZ')} Kč</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-teal-200">
                    <span className="text-slate-900 font-bold">Celkem volitelné položky</span>
                    <span className="font-bold text-teal-600 text-lg">{optionalTotal.toLocaleString('cs-CZ')} Kč</span>
                  </div>
                </div>
              </div>
            )}

            {/* Celkem */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-xl p-8 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Celková orientační cena</h3>
                  <p className="text-blue-100 text-sm">
                    {optionalTotal > 0 
                      ? 'Základní nabídka + vybrané volitelné položky' 
                      : 'Kompletní realizace akce včetně cateringu, inventáře, programu a produkce'
                    }
                  </p>
                  {optionalTotal > 0 && (
                    <p className="text-blue-100 text-xs mt-1">
                      Základní cena: {(cateringTotal + 162000 + 12000).toLocaleString('cs-CZ')} Kč + Volitelné: {optionalTotal.toLocaleString('cs-CZ')} Kč + Produkce (20%): {productionFee.toLocaleString('cs-CZ')} Kč
                    </p>
                  )}
                  <p className="text-blue-100 text-xs mt-2">
                    * Ceny jsou orientační a mohou se lišit dle finálního výběru a počtu hostů
                  </p>
                  {optionalTotal === 0 && (
                    <p className="text-blue-100 text-xs">
                      * Kliknutím na aktivity a program můžete přidat volitelné položky
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold mb-2">
                    {(cateringTotal + 162000 + 12000 + optionalTotal + productionFee).toLocaleString('cs-CZ')} Kč
                  </div>
                  <div className="text-blue-100 text-sm mb-1">bez DPH</div>
                  <div className="text-blue-200 text-lg font-semibold">
                    {Math.round((cateringTotal + 162000 + 12000 + optionalTotal + productionFee) / guestCount).toLocaleString('cs-CZ')} Kč / osoba
                  </div>
                </div>
              </div>
            </div>
            
            {/* PDF Export Button */}
            <div className="text-center mt-12">
              <button
                onClick={handleDownloadFullOfferPDF}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <Download className="w-5 h-5" />
                <span>Exportovat rozpočet do PDF</span>
              </button>
            </div>
          </div>
        </div>
      </section>


      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 via-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12 text-slate-800">
            Proč organizovat event s námi?
          </h2>
          
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border-2 border-orange-100">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                <strong>Nejsme pouze produkční agentura.</strong> Jsme <strong className="text-orange-600">realizační společnost</strong> s vlastním technickým i cateringovým zázemím a týmem zkušených profesionálů – od kuchařů a techniků až po produkční a logistiku.
              </p>
              
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Díky vlastnímu vybavení a internímu týmu máme <strong>celý event plně pod kontrolou</strong>. Nejsme závislí na externích dodavatelích, což nám umožňuje garantovat kvalitu, přesné časování i transparentní rozpočet bez zbytečných provizí.
              </p>
              
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Klient tak získává <strong className="text-orange-600">partnera, který má realizaci skutečně ve vlastních rukou</strong> – od prvního návrhu až po poslední detail akce.
              </p>
              
              <p className="text-lg text-slate-700 leading-relaxed font-semibold mb-8">
                Za kvalitu i průběh realizace <strong className="text-orange-600">osobně ručím</strong>.
              </p>
              
              {/* Signature */}
              <div className="mt-6 pt-4 border-t-2 border-orange-100">
                <div className="flex flex-col items-start">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vsepro-wanzl/artifacts/ma5n5v8f_3abc18410f8bee0072c271746b29827a56e90d8078464c9dbb3b30545f73260d.png" 
                    alt="Podpis Jakuba Vodárka" 
                    className="w-80 h-auto object-contain -mb-2"
                  />
                  <div className="text-sm text-slate-500 font-medium">
                    Jednatel společnosti
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section - Custom for PROMENS */}
      <PromensContactSection />
      
      {/* Footer - Custom for PROMENS */}
      <PromensFooter />
    </div>
  );
};

export default PromensOfferPage;
